import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, MessageSquare } from "lucide-react";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const channels = [
    {
        icon: Phone,
        label: "Call",
        value: BRAND.phone,
        href: BRAND.phoneHref,
    },
    {
        icon: MessageSquare,
        label: "Text us",
        value: BRAND.phone,
        href: BRAND.smsHref,
    },
    {
        icon: Mail,
        label: "Email",
        value: BRAND.email,
        href: `mailto:${BRAND.email}`,
    },
    {
        icon: MapPin,
        label: "Service area",
        value: BRAND.address,
    },
    {
        icon: Clock,
        label: "Hours",
        value: "Mon–Sat · 07:00 – 19:00 · Roadside 24/7",
    },
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please add your name, email and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message sent — we'll reply within the hour.");
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error("Could not send — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full bg-white border border-[#0a0a0a]/15 px-4 py-3.5 text-[15px] focus:outline-none focus:border-[#0a0a0a] placeholder:text-[#a1a1aa] transition-colors";

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="border-b hairline"
        >
            <div className="mx-auto max-w-[1400px] grid grid-cols-12">
                <div className="col-span-12 lg:col-span-5 px-6 md:px-12 py-14 md:py-20 border-r hairline">
                    <div className="font-mono-tech text-[11px] text-[#1d4ed8] mb-6">
                        CONTACT
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95]">
                        Got a question?
                        <br />
                        Talk to a mechanic.
                    </h2>
                    <div className="mt-10 border-t border-l hairline">
                        {channels.map((c) => {
                            const Icon = c.icon;
                            const inner = (
                                <div className="flex items-start gap-4 p-5">
                                    <Icon
                                        className="w-5 h-5 text-[#1d4ed8] mt-0.5 shrink-0"
                                        strokeWidth={1.5}
                                    />
                                    <div>
                                        <div className="font-mono-tech text-[10px] text-[#52525b]">
                                            {c.label}
                                        </div>
                                        <div className="text-[15px] mt-1 font-medium">
                                            {c.value}
                                        </div>
                                    </div>
                                </div>
                            );
                            return c.href ? (
                                <a
                                    key={c.label}
                                    href={c.href}
                                    data-testid={`contact-channel-${c.label.toLowerCase()}`}
                                    className="block border-r border-b hairline hover:bg-[#0a0a0a] hover:text-white transition-colors"
                                >
                                    {inner}
                                </a>
                            ) : (
                                <div
                                    key={c.label}
                                    data-testid={`contact-channel-${c.label.toLowerCase()}`}
                                    className="border-r border-b hairline"
                                >
                                    {inner}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="col-span-12 lg:col-span-7 p-6 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-5"
                >
                    <div>
                        <label className="font-mono-tech text-[10px] text-[#52525b] block mb-2">
                            Name
                        </label>
                        <input
                            data-testid="contact-name"
                            className={inputCls}
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            placeholder="Your name"
                        />
                    </div>
                    <div>
                        <label className="font-mono-tech text-[10px] text-[#52525b] block mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            data-testid="contact-email"
                            className={inputCls}
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            placeholder="you@email.com"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="font-mono-tech text-[10px] text-[#52525b] block mb-2">
                            Phone (optional)
                        </label>
                        <input
                            data-testid="contact-phone"
                            className={inputCls}
                            value={form.phone}
                            onChange={(e) => set("phone", e.target.value)}
                            placeholder="+1 555 555 5555"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="font-mono-tech text-[10px] text-[#52525b] block mb-2">
                            Message
                        </label>
                        <textarea
                            data-testid="contact-message"
                            rows={5}
                            className={inputCls + " resize-none"}
                            value={form.message}
                            onChange={(e) => set("message", e.target.value)}
                            placeholder="How can we help?"
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit"
                            className="btn-primary px-7 py-4 font-mono-tech text-[12px] inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Sending…
                                </>
                            ) : (
                                "Send message →"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
